import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AdminService from "../services/AdminService";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon issue with webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const MOCK_COUNTRIES = ["Saint Kitts And Nevis", "Trinidad And Tobago", "Jamaica", "Barbados", "Bahamas", "Grenada"];

// Default map center (Caribbean)
const DEFAULT_CENTER = [17.35, -62.78];

// Draggable marker component
const DraggableMarker = ({ position, onDragEnd }) => {
    const markerRef = useRef(null);

    const eventHandlers = {
        dragend() {
            const marker = markerRef.current;
            if (marker) {
                const latlng = marker.getLatLng();
                onDragEnd(latlng.lat, latlng.lng);
            }
        },
    };

    return (
        <Marker
            draggable={true}
            eventHandlers={eventHandlers}
            position={position}
            ref={markerRef}
        />
    );
};

// Component to recenter map when position changes
const MapUpdater = ({ center }) => {
    const map = useMapEvents({});
    useEffect(() => {
        if (center && center[0] && center[1]) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
};

const AdEditPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const locationSearchTimeout = useRef(null);

    const [form, setForm] = useState({
        title: "", description: "", slug: "", categoryId: "", subCategoryId: "",
        price: "", contactName: "", contactPhone: "", hideMobile: false,
        state: "", city: "", country: "", lat: "", lng: "", location: "",
        countryId: "", stateId: "", cityId: "",
        countryList: [], featured: false, status: true, expireDate: "",
    });

    const [categories, setCategories] = useState([]);
    const [locationCountries, setLocationCountries] = useState([]);
    const [locationStates, setLocationStates] = useState([]);
    const [locationCities, setLocationCities] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [errors, setErrors] = useState({});
    const [dragOver, setDragOver] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [countriesLoaded, setCountriesLoaded] = useState(false);
    const [mapPosition, setMapPosition] = useState(DEFAULT_CENTER);

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await AdminService.getCategories();
                const data = res.data?.data || res.data;
                if (Array.isArray(data)) setCategories(data);
            } catch (err) {
                console.error("Failed to fetch categories:", err);
                setCategories([]);
            } finally {
                setCategoriesLoaded(true);
            }
        };
        fetchCategories();
    }, []);

    // Fetch location countries
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await AdminService.getLocationCountries();
                const responseData = res.data?.data || res.data;
                // API returns { data: { country: [...] } }
                const countries = responseData?.country || responseData;
                if (Array.isArray(countries)) {
                    setLocationCountries(countries);
                }
            } catch (err) {
                console.error("Failed to fetch countries:", err);
            } finally {
                setCountriesLoaded(true);
            }
        };
        fetchCountries();
    }, []);

    // Fetch ad data only after categories AND countries are loaded
    const fetchAd = useCallback(async () => {
        if (!categoriesLoaded || !countriesLoaded) return;

        setLoading(true);
        try {
            const res = await AdminService.getAdById(id);
            const ad = res.data?.data || res.data;

            const formatDate = (d) => {
                if (!d) return "";
                return new Date(d).toISOString().split("T")[0];
            };

            const catId = ad.subCategory?.parentCategory?.id?.toString() || "";
            const subCatId = ad.subCategory?.id?.toString() || "";
            const adCountry = ad.productAddresses?.country || "";
            const adState = ad.productAddresses?.state || "";
            const adCity = ad.productAddresses?.city || "";
            const adLat = ad.productAddresses?.lat?.toString() || "";
            const adLng = ad.productAddresses?.lng?.toString() || "";

            // Find countryId by matching country name
            let matchedCountryId = "";
            let matchedStateId = "";
            let matchedCityId = "";
            let fetchedStates = [];
            let fetchedCities = [];

            if (adCountry && locationCountries.length > 0) {
                const matchedCountry = locationCountries.find(
                    (c) => c.name.toLowerCase() === adCountry.toLowerCase()
                );
                if (matchedCountry) {
                    matchedCountryId = matchedCountry.id.toString();
                    const cca2 = matchedCountry.cca2;

                    // Fetch states for this country using cca2
                    try {
                        const statesRes = await AdminService.getStatesByCca2(cca2);
                        const statesData = statesRes.data?.data || statesRes.data;
                        fetchedStates = Array.isArray(statesData) ? statesData : [];

                        if (fetchedStates.length > 0) {
                            setLocationStates(fetchedStates);

                            // Find stateId by matching state name
                            if (adState) {
                                const matchedState = fetchedStates.find(
                                    (s) => s.name.toLowerCase() === adState.toLowerCase()
                                );
                                if (matchedState) {
                                    matchedStateId = matchedState.id.toString();

                                    // Fetch cities for this state
                                    try {
                                        const citiesRes = await AdminService.getCitiesByStateId(matchedState.id);
                                        const citiesData = citiesRes.data?.data || citiesRes.data;
                                        fetchedCities = Array.isArray(citiesData) ? citiesData : [];

                                        if (fetchedCities.length > 0) {
                                            setLocationCities(fetchedCities);

                                            // Find cityId by matching city name
                                            if (adCity) {
                                                const matchedCity = fetchedCities.find(
                                                    (c) => c.name.toLowerCase() === adCity.toLowerCase()
                                                );
                                                if (matchedCity) {
                                                    matchedCityId = matchedCity.id.toString();
                                                }
                                            }
                                        }
                                    } catch (e) {
                                        console.error("Failed to fetch cities for edit:", e);
                                    }
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Failed to fetch states for edit:", e);
                    }
                }
            }

            // Update map position
            if (adLat && adLng) {
                setMapPosition([parseFloat(adLat), parseFloat(adLng)]);
            }

            setForm({
                title: ad.title || "",
                description: ad.description || "",
                slug: ad.slug || "",
                categoryId: catId,
                subCategoryId: subCatId,
                price: ad.price?.toString() || "",
                contactName: ad.contactName || "",
                contactPhone: ad.contactPhone || "",
                hideMobile: ad.hideMobile || false,
                state: adState,
                city: adCity,
                country: adCountry,
                lat: adLat,
                lng: adLng,
                location: ad.productAddresses?.location || "",
                countryId: matchedCountryId,
                stateId: matchedStateId,
                cityId: matchedCityId,
                countryList: ad.countryList || [],
                featured: ad.featured || false,
                status: ad.status !== false,
                expireDate: formatDate(ad.expireDate),
            });

            // Extract existing images
            if (ad.productImages && ad.productImages.length > 0) {
                setExistingImages(ad.productImages.map((img) => ({
                    url: typeof img === "string" ? img : img.imageUrl || img.url || img,
                    name: typeof img === "string" ? "image" : img.name || "image",
                })));
            }
        } catch (err) {
            console.error("Failed to fetch ad:", err);
            toast.error("Failed to load ad details");
        } finally {
            setLoading(false);
        }
    }, [id, categoriesLoaded, countriesLoaded, locationCountries]);

    useEffect(() => {
        fetchAd();
    }, [fetchAd]);

    const getSubcategories = () => {
        const cat = categories.find((c) => c.id === parseInt(form.categoryId));
        return cat?.subCategory || cat?.subCategories || cat?.subcategories || [];
    };

    // Fetch states when countryId changes (user interaction only)
    const fetchStatesForCountry = async (countryId) => {
        if (!countryId) {
            setLocationStates([]);
            setLocationCities([]);
            return;
        }
        try {
            const selectedCountry = locationCountries.find((c) => c.id === parseInt(countryId));
            if (selectedCountry) {
                const res = await AdminService.getStatesByCca2(selectedCountry.cca2);
                const data = res.data?.data || res.data;
                if (Array.isArray(data)) setLocationStates(data);
                else setLocationStates([]);
            }
        } catch (err) {
            console.error("Failed to fetch states:", err);
            setLocationStates([]);
        }
        setLocationCities([]);
    };

    // Fetch cities when stateId changes (user interaction only)
    const fetchCitiesForState = async (stateId) => {
        if (!stateId) {
            setLocationCities([]);
            return;
        }
        try {
            const res = await AdminService.getCitiesByStateId(stateId);
            const data = res.data?.data || res.data;
            if (Array.isArray(data)) setLocationCities(data);
            else setLocationCities([]);
        } catch (err) {
            console.error("Failed to fetch cities:", err);
            setLocationCities([]);
        }
    };

    const handleChange = (field, value) => {
        const updates = { [field]: value };
        if (field === "title") {
            updates.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        }
        if (field === "categoryId") {
            updates.subCategoryId = "";
        }
        if (field === "countryId") {
            // When country changes, set country name and clear state/city
            const selectedCountry = locationCountries.find((c) => c.id === parseInt(value));
            updates.country = selectedCountry ? selectedCountry.name : "";
            updates.stateId = "";
            updates.state = "";
            updates.cityId = "";
            updates.city = "";
            fetchStatesForCountry(value);
        }
        if (field === "stateId") {
            // When state changes, set state name and clear city
            const selectedState = locationStates.find((s) => s.id === parseInt(value));
            updates.state = selectedState ? selectedState.name : "";
            updates.cityId = "";
            updates.city = "";
            fetchCitiesForState(value);
        }
        if (field === "cityId") {
            // When city changes, set city name
            const selectedCity = locationCities.find((c) => c.id === parseInt(value));
            updates.city = selectedCity ? selectedCity.name : "";
        }
        setForm((prev) => ({ ...prev, ...updates }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    };

    // Location search geocoding (Nominatim)
    const handleLocationSearch = (value) => {
        setForm((prev) => ({ ...prev, location: value }));
        if (errors.location) setErrors((prev) => ({ ...prev, location: null }));

        // Debounce the geocoding search
        if (locationSearchTimeout.current) {
            clearTimeout(locationSearchTimeout.current);
        }

        if (!value.trim()) return;

        locationSearchTimeout.current = setTimeout(async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=1`
                );
                const results = await response.json();
                if (results && results.length > 0) {
                    const { lat, lon } = results[0];
                    const newLat = parseFloat(lat).toFixed(6);
                    const newLng = parseFloat(lon).toFixed(6);
                    setForm((prev) => ({
                        ...prev,
                        lat: newLat,
                        lng: newLng,
                    }));
                    setMapPosition([parseFloat(lat), parseFloat(lon)]);
                }
            } catch (err) {
                console.error("Geocoding failed:", err);
            }
        }, 800);
    };

    // Handle marker drag - only update lat/lng, NOT location text
    const handleMarkerDragEnd = (lat, lng) => {
        const newLat = lat.toFixed(6);
        const newLng = lng.toFixed(6);
        setForm((prev) => ({
            ...prev,
            lat: newLat,
            lng: newLng,
        }));
        setMapPosition([lat, lng]);
    };

    const handleCountryToggle = (country) => {
        setForm((prev) => ({
            ...prev,
            countryList: prev.countryList.includes(country)
                ? prev.countryList.filter((c) => c !== country)
                : [...prev.countryList, country],
        }));
    };

    // Image handling
    const handleFiles = (fileList) => {
        const total = existingImages.length + newImages.length + fileList.length;
        if (total > 6) { toast.error("Maximum 6 images allowed"); return; }
        const files = Array.from(fileList).filter((f) => {
            if (!f.type.startsWith("image/")) { toast.error(`"${f.name}" is not an image`); return false; }
            if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" exceeds 10MB`); return false; }
            return true;
        });
        const imgs = files.map((file) => ({ file, preview: URL.createObjectURL(file), name: file.name }));
        setNewImages((prev) => [...prev, ...imgs]);
    };

    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

    const removeExistingImage = (index) => {
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
    };

    const removeNewImage = (index) => {
        setNewImages((prev) => {
            const updated = [...prev];
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });
    };

    const validate = () => {
        const err = {};
        if (!form.title.trim()) err.title = "Title is required";
        if (!form.description.trim()) err.description = "Description is required";
        if (!form.subCategoryId) err.subCategoryId = "Select a subcategory";
        if (!form.contactName.trim()) err.contactName = "Contact name is required";
        if (!form.contactPhone.trim()) err.contactPhone = "Contact phone is required";
        if (!form.state.trim()) err.state = "State is required";
        if (!form.country.trim()) err.country = "Country is required";
        if (form.countryList.length === 0) err.countryList = "Select at least one country";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validate()) { toast.error("Please fix validation errors"); return; }

        setSaving(true);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                slug: form.slug,
                subCategoryId: form.subCategoryId ? parseInt(form.subCategoryId) : null,
                price: form.price ? parseFloat(form.price) : null,
                contactName: form.contactName,
                contactPhone: form.contactPhone,
                hide: form.hideMobile,
                state: form.state,
                city: form.city,
                country: form.country,
                lat: form.lat ? parseFloat(form.lat) : null,
                lng: form.lng ? parseFloat(form.lng) : null,
                location: form.location,
                countryList: form.countryList,
                featured: form.featured,
            };

            await AdminService.updateAd(id, payload);
            toast.success("Ad updated successfully!");
            navigate(`/admin/ads/view/${id}`);
        } catch (err) {
            console.error("Failed to update ad:", err);
            toast.error("Failed to update ad: " + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div>
                <div className="admin-breadcrumb">
                    <Link to="/admin/ads">Ads</Link>
                    <i className="fa fa-angle-right" />
                    <span>Loading...</span>
                </div>
                <div className="admin-card">
                    <div className="admin-card-body">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="admin-skeleton-row">
                                <div style={{ flex: 1 }}>
                                    <div className="admin-skeleton admin-skeleton-text" />
                                    <div className="admin-skeleton admin-skeleton-text short" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="admin-breadcrumb">
                <Link to="/admin/ads">Ads</Link>
                <i className="fa fa-angle-right" />
                <Link to={`/admin/ads/view/${id}`}>#{id}</Link>
                <i className="fa fa-angle-right" />
                <span>Edit</span>
            </div>

            <div className="admin-page-header">
                <h2>Edit Ad #{id}</h2>
                <div className="admin-page-actions">
                    <button className="admin-btn admin-btn-outline" onClick={() => navigate(`/admin/ads/view/${id}`)}>
                        <i className="fa fa-eye" /> View Ad
                    </button>
                    <button className="admin-btn admin-btn-primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin" /> Saving...</> : <><i className="fa fa-save" /> Update Ad</>}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
                    {/* Left Column */}
                    <div>
                        {/* Basic Info */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-info-circle" /> Basic Information</div>
                                <div className="admin-form-group">
                                    <label>Title *</label>
                                    <input type="text" className="admin-form-control" value={form.title}
                                        onChange={(e) => handleChange("title", e.target.value)} maxLength={100} />
                                    {errors.title && <div className="admin-form-error">{errors.title}</div>}
                                    <div className="admin-form-helper">{form.title.length}/100</div>
                                </div>
                                <div className="admin-form-group">
                                    <label>Slug</label>
                                    <input type="text" className="admin-form-control" value={form.slug}
                                        onChange={(e) => handleChange("slug", e.target.value)} />
                                </div>
                                <div className="admin-form-group">
                                    <label>Description *</label>
                                    <textarea className="admin-form-control" rows="6" value={form.description}
                                        onChange={(e) => handleChange("description", e.target.value)} maxLength={5000}
                                        style={{ resize: "vertical" }} />
                                    {errors.description && <div className="admin-form-error">{errors.description}</div>}
                                    <div className="admin-form-helper">{form.description.length}/5000</div>
                                </div>
                            </div>
                        </div>

                        {/* Category */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-th-large" /> Category</div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Category *</label>
                                        <select className="admin-form-control" value={form.categoryId}
                                            onChange={(e) => handleChange("categoryId", e.target.value)}>
                                            <option value="">Select Category</option>
                                            {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Subcategory *</label>
                                        <select className="admin-form-control" value={form.subCategoryId}
                                            onChange={(e) => handleChange("subCategoryId", e.target.value)}
                                            disabled={!form.categoryId}>
                                            <option value="">Select Subcategory</option>
                                            {getSubcategories().map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                                        </select>
                                        {errors.subCategoryId && <div className="admin-form-error">{errors.subCategoryId}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-usd" /> Pricing</div>
                                <div className="admin-form-group">
                                    <label>Price (USD)</label>
                                    <input type="number" className="admin-form-control" value={form.price}
                                        onChange={(e) => handleChange("price", e.target.value)} min="0" step="0.01" />
                                </div>
                            </div>
                        </div>

                        {/* Media */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-image" /> Media</div>

                                {/* Existing images */}
                                {existingImages.length > 0 && (
                                    <>
                                        <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, display: "block" }}>Current Images</label>
                                        <div className="admin-upload-preview" style={{ marginBottom: 20 }}>
                                            {existingImages.map((img, i) => (
                                                <div key={i} className="admin-upload-preview-item">
                                                    <img src={img.url} alt={img.name}
                                                        onError={(e) => { e.target.src = "https://via.placeholder.com/100x100?text=Error"; }} />
                                                    <button type="button" className="admin-upload-preview-remove" onClick={() => removeExistingImage(i)}>
                                                        <i className="fa fa-times" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Upload new */}
                                <div className={`admin-upload-zone ${dragOver ? "dragover" : ""}`}
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}>
                                    <i className="fa fa-cloud-upload" />
                                    <p>Add more images — <span>browse</span> or drag & drop</p>
                                    <small>{existingImages.length + newImages.length}/6 used</small>
                                    <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                                        onChange={(e) => handleFiles(e.target.files)} />
                                </div>

                                {newImages.length > 0 && (
                                    <div className="admin-upload-preview" style={{ marginTop: 12 }}>
                                        {newImages.map((img, i) => (
                                            <div key={i} className="admin-upload-preview-item">
                                                <img src={img.preview} alt={img.name} />
                                                <button type="button" className="admin-upload-preview-remove" onClick={() => removeNewImage(i)}>
                                                    <i className="fa fa-times" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-address-card" /> Contact</div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Contact Name *</label>
                                        <input type="text" className="admin-form-control" value={form.contactName}
                                            onChange={(e) => handleChange("contactName", e.target.value)} maxLength={65} />
                                        {errors.contactName && <div className="admin-form-error">{errors.contactName}</div>}
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Contact Phone *</label>
                                        <input type="text" className="admin-form-control" value={form.contactPhone}
                                            onChange={(e) => handleChange("contactPhone", e.target.value)} />
                                        {errors.contactPhone && <div className="admin-form-error">{errors.contactPhone}</div>}
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.hideMobile}
                                                onChange={(e) => handleChange("hideMobile", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Hide mobile from public</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Location */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-map-marker" /> Location</div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Country *</label>
                                        <select className="admin-form-control" value={form.countryId}
                                            onChange={(e) => handleChange("countryId", e.target.value)}>
                                            <option value="">Select Country</option>
                                            {locationCountries.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                        {errors.country && <div className="admin-form-error">{errors.country}</div>}
                                    </div>
                                    <div className="admin-form-group">
                                        <label>State *</label>
                                        <select className="admin-form-control" value={form.stateId}
                                            onChange={(e) => handleChange("stateId", e.target.value)}
                                            disabled={!form.countryId}>
                                            <option value="">Select State</option>
                                            {locationStates.map((s) => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                        {errors.state && <div className="admin-form-error">{errors.state}</div>}
                                    </div>
                                </div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>City</label>
                                        <select className="admin-form-control" value={form.cityId}
                                            onChange={(e) => handleChange("cityId", e.target.value)}
                                            disabled={!form.stateId}>
                                            <option value="">Select City</option>
                                            {locationCities.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Location Text</label>
                                        <input type="text" className="admin-form-control" value={form.location}
                                            onChange={(e) => handleLocationSearch(e.target.value)}
                                            placeholder="Type area name to search on map..." />
                                    </div>
                                </div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Latitude</label>
                                        <input type="number" className="admin-form-control" value={form.lat}
                                            onChange={(e) => handleChange("lat", e.target.value)} step="any" />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Longitude</label>
                                        <input type="number" className="admin-form-control" value={form.lng}
                                            onChange={(e) => handleChange("lng", e.target.value)} step="any" />
                                    </div>
                                </div>

                                {/* Map */}
                                <div style={{ marginTop: 16, borderRadius: 8, overflow: "hidden", border: "1px solid #e0e0e0" }}>
                                    <MapContainer
                                        center={mapPosition}
                                        zoom={13}
                                        style={{ height: 300, width: "100%" }}
                                        scrollWheelZoom={true}
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <DraggableMarker
                                            position={mapPosition}
                                            onDragEnd={handleMarkerDragEnd}
                                        />
                                        <MapUpdater center={mapPosition} />
                                    </MapContainer>
                                </div>
                                <div className="admin-form-helper" style={{ marginTop: 6 }}>
                                    📍 Type in "Location Text" to search, or drag the marker to set coordinates manually.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div>
                        {/* Status */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-cog" /> Status & Settings</div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.status}
                                                onChange={(e) => handleChange("status", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Active</span>
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.featured}
                                                onChange={(e) => handleChange("featured", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Featured Ad ⭐</span>
                                    </div>
                                </div>
                                <div className="admin-form-group" style={{ marginTop: 20 }}>
                                    <label>Expiry Date</label>
                                    <input type="date" className="admin-form-control" value={form.expireDate}
                                        onChange={(e) => handleChange("expireDate", e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {/* Country Visibility */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-globe" /> Country Visibility</div>
                                {MOCK_COUNTRIES.map((country) => (
                                    <div key={country} className="admin-form-group" style={{ marginBottom: 8 }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontWeight: 400 }}>
                                            <input type="checkbox" className="admin-checkbox"
                                                checked={form.countryList.includes(country)}
                                                onChange={() => handleCountryToggle(country)} />
                                            {country}
                                        </label>
                                    </div>
                                ))}
                                {errors.countryList && <div className="admin-form-error">{errors.countryList}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="admin-card" style={{ marginTop: 10 }}>
                    <div className="admin-card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <button type="button" className="admin-btn admin-btn-outline" onClick={() => navigate("/admin/ads")}>
                            <i className="fa fa-arrow-left" /> Back to Ads
                        </button>
                        <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                            {saving ? <><i className="fa fa-spinner fa-spin" /> Updating...</> : <><i className="fa fa-save" /> Update Ad</>}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdEditPage;
